/* AdminBankRail — Bank Rail ops command center (Iter 162).
 *
 * Single-page god-view of money in flight across every payment rail
 * (ACH/Shield, Wire, Card/Stripe). Sections, top-down:
 *
 *   1. Totals strip (volume today · txn count · stuck count)
 *   2. Per-rail status board (health, today's volume, today's success%)
 *   3. Stuck transactions queue (pending > 6h) — Retry / Reroute
 *   4. Reconciliation panel (Rail webhooks vs internal ledger)
 *   5. Per-rail fee config (editable + audited)
 *   6. Audit log (paginated, append-only)
 *
 * Mounted at /admin/bank-rail. Admin-only.
 */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Banknote, Send, Zap, CreditCard, Sparkles, RefreshCw, Activity,
  AlertTriangle, CheckCircle2, ShieldAlert, ArrowRight, Wrench, ScrollText,
  TrendingUp, Settings2, X,
} from "lucide-react";
import { PageHeader } from "../../components/PortalShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const ICONS = { Banknote, Send, Zap, CreditCard, Sparkles };

const STATUS_TONE = {
  up:              { color: "emerald", label: "Healthy",         dot: "bg-emerald-500" },
  degraded:        { color: "amber",   label: "Degraded",        dot: "bg-amber-500" },
  down:            { color: "rose",    label: "Down",            dot: "bg-rose-500" },
  idle:            { color: "slate",   label: "Idle",            dot: "bg-slate-400" },
  not_configured:  { color: "slate",   label: "Not configured",  dot: "bg-slate-300" },
};

const fmtUsd = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const fmtUsdP = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);

export default function AdminBankRail() {
  const [overview, setOverview] = useState(null);
  const [stuck, setStuck] = useState(null);
  const [recon, setRecon] = useState(null);
  const [fees, setFees] = useState(null);
  const [audit, setAudit] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [actionTxn, setActionTxn] = useState(null);     // {txn, kind: 'retry'|'reroute'}

  const loadAll = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const [o, s, r, f, a] = await Promise.all([
        api.get("/admin/bank-rail/overview"),
        api.get("/admin/bank-rail/stuck"),
        api.get("/admin/bank-rail/reconciliation"),
        api.get("/admin/bank-rail/fees"),
        api.get("/admin/bank-rail/audit"),
      ]);
      setOverview(o.data); setStuck(s.data); setRecon(r.data);
      setFees(f.data); setAudit(a.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Bank Rail data");
    } finally { if (!silent) setRefreshing(false); }
  };

  useEffect(() => { loadAll(); }, []);
  // Auto-refresh the overview every 30s so the status board stays live.
  useEffect(() => {
    const id = setInterval(() => {
      api.get("/admin/bank-rail/overview").then(r => setOverview(r.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid="admin-bank-rail" className="space-y-5">
      <PageHeader
        title="Bank Rail"
        subtitle="Money in flight across every rail · health, stuck transactions, reconciliation, and fee config."
        action={
          <Button variant="outline" onClick={() => loadAll()} disabled={refreshing}
                  data-testid="bank-rail-refresh" className="rounded-full">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`}/>
            Refresh
          </Button>
        }
      />

      <div className="p-8 space-y-6">

        {/* 1 · Totals strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bank-rail-totals">
          <KpiSm label="Volume today"    value={fmtUsd(overview?.totals?.volume_today_usd)}
                 tone="violet"  icon={TrendingUp}/>
          <KpiSm label="Transactions today" value={overview?.totals?.txn_count_today ?? "—"}
                 tone="blue"    icon={Activity}/>
          <KpiSm label={`Stuck (> ${overview?.totals?.stuck_threshold_h ?? 6}h)`}
                 value={overview?.totals?.stuck_count ?? "—"}
                 tone={(overview?.totals?.stuck_count ?? 0) > 0 ? "rose" : "emerald"}
                 icon={AlertTriangle}/>
          <KpiSm label="Recon mismatches"
                 value={
                   recon ? ((recon.webhook_no_ledger_count || 0)
                          + (recon.ledger_no_webhook_count || 0)
                          + (recon.status_mismatch_count   || 0)) : "—"
                 }
                 tone={(recon && (recon.webhook_no_ledger_count + recon.ledger_no_webhook_count + recon.status_mismatch_count) > 0)
                   ? "amber" : "emerald"}
                 icon={ShieldAlert}/>
        </div>

        {/* 2 · Per-rail status board */}
        <section className="rounded-2xl border border-border bg-white p-5" data-testid="bank-rail-status-board">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Rail status board</div>
              <div className="text-[14px] font-medium mt-0.5">Live health · last 1 hour</div>
            </div>
            <span className="text-[11px] text-foreground/55 mono">
              {overview?.generated_at ? new Date(overview.generated_at).toLocaleTimeString() : "—"}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(overview?.rails || []).map((r) => {
              const Icon = ICONS[r.icon] || Banknote;
              const tone = STATUS_TONE[r.health?.status] || STATUS_TONE.idle;
              return (
                <div key={r.id}
                     className="rounded-xl border border-border bg-secondary/20 p-3.5"
                     data-testid={`rail-card-${r.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-7 h-7 rounded-lg grid place-items-center bg-${r.tone}-50 text-${r.tone}-700 border border-${r.tone}-200`}>
                        <Icon className="w-3.5 h-3.5"/>
                      </span>
                      <div>
                        <div className="text-[13px] font-medium">{r.label}</div>
                        <div className="text-[10.5px] text-foreground/55 mono">{r.settles}</div>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] mono uppercase tracking-wider border bg-${tone.color}-50 text-${tone.color}-800 border-${tone.color}-200`}
                          data-testid={`rail-status-${r.id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`}/>{tone.label}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <Mini label="Volume" value={fmtUsd(r.today_volume_usd)}/>
                    <Mini label="Txns"   value={r.today_txn_count}/>
                    <Mini label="Success" value={r.today_success_pct != null ? `${r.today_success_pct}%` : "—"}/>
                  </div>
                  {r.health?.last_error && (
                    <div className="mt-2 text-[10.5px] text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1 leading-tight"
                         data-testid={`rail-error-${r.id}`}>
                      <strong>Last error:</strong> {r.health.last_error}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* 3 · Stuck transactions */}
        <section className="rounded-2xl border border-border bg-white" data-testid="bank-rail-stuck">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div>
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Stuck transactions</div>
              <div className="text-[14px] font-medium mt-0.5">
                Pending &gt; {stuck?.threshold_hours ?? 6}h · {stuck?.count ?? 0} flagged
              </div>
            </div>
            {(stuck?.count || 0) === 0 && (
              <span className="inline-flex items-center gap-1.5 text-[12px] text-emerald-700">
                <CheckCircle2 className="w-3.5 h-3.5"/>All caught up
              </span>
            )}
          </div>
          {(stuck?.count || 0) === 0 ? (
            <div className="px-5 py-10 text-center text-[13px] text-foreground/55" data-testid="stuck-empty">
              No stuck transactions in the queue.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {(stuck?.rows || []).map((t) => (
                <div key={t.id} className="px-5 py-3 flex items-center gap-4"
                     data-testid={`stuck-row-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-medium truncate">
                      {t.user?.company || t.user?.email || "—"}
                      <span className="ml-2 text-[10.5px] mono uppercase tracking-wider text-foreground/55">
                        {t.type}
                      </span>
                    </div>
                    <div className="text-[11.5px] text-foreground/55 mt-0.5 mono">
                      {t.ref} · {t.rail.toUpperCase()} · {t.status} · {t.age_hours}h old
                    </div>
                  </div>
                  <div className="text-[13.5px] tabular-nums">{fmtUsdP(t.amount_usd)}</div>
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline"
                            data-testid={`stuck-retry-${t.id}`}
                            onClick={() => setActionTxn({ txn: t, kind: "retry" })}
                            className="rounded-full text-[11.5px] h-7 px-3">
                      <Wrench className="w-3 h-3 mr-1"/>Retry
                    </Button>
                    <Button size="sm" variant="outline"
                            data-testid={`stuck-reroute-${t.id}`}
                            onClick={() => setActionTxn({ txn: t, kind: "reroute" })}
                            className="rounded-full text-[11.5px] h-7 px-3">
                      <ArrowRight className="w-3 h-3 mr-1"/>Reroute
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* 4 · Reconciliation */}
        <ReconciliationPanel recon={recon}/>

        {/* 5 · Fee config */}
        <FeeConfigPanel fees={fees} onSaved={() => loadAll()}/>

        {/* 6 · Audit log */}
        <AuditLogPanel audit={audit}/>
      </div>

      <ActionDialog
        open={!!actionTxn}
        action={actionTxn}
        onClose={() => setActionTxn(null)}
        onDone={() => { setActionTxn(null); loadAll(); }}
      />
    </div>
  );
}

function KpiSm({ label, value, tone = "slate", icon: Icon }) {
  return (
    <div className={`rounded-xl border bg-${tone}-50 border-${tone}-200 px-4 py-3 flex items-center justify-between`}>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-foreground/55">{label}</div>
        <div className="text-2xl font-semibold tabular-nums mt-0.5">{value}</div>
      </div>
      {Icon && <Icon className={`w-5 h-5 text-${tone}-500`}/>}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded-md bg-white border border-border px-2.5 py-1.5">
      <div className="text-[9.5px] mono uppercase tracking-wider text-foreground/55">{label}</div>
      <div className="text-[12.5px] font-medium tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function ReconciliationPanel({ recon }) {
  if (!recon) return null;
  const sections = [
    { id: "webhook_no_ledger", label: "Webhook · no ledger row",
      rows: recon.webhook_no_ledger, count: recon.webhook_no_ledger_count,
      hint: "The rail notified us, but we never recorded the transaction. Likely a lost webhook earlier in the chain." },
    { id: "ledger_no_webhook", label: "Ledger · no webhook",
      rows: recon.ledger_no_webhook, count: recon.ledger_no_webhook_count,
      hint: "We marked the transaction complete, but the rail never confirmed via webhook. Check the provider dashboard manually." },
    { id: "status_mismatch",   label: "Status mismatch (webhook SUCCESS vs ledger PENDING)",
      rows: recon.status_mismatch, count: recon.status_mismatch_count,
      hint: "Webhook says SUCCESS, ledger still PENDING. Could be a missed status-promotion job." },
  ];
  return (
    <section className="rounded-2xl border border-border bg-white" data-testid="bank-rail-recon">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Reconciliation</div>
          <div className="text-[14px] font-medium mt-0.5">
            Rail webhooks vs internal ledger · last {recon.window_days}d
          </div>
        </div>
      </div>
      <div className="divide-y divide-border">
        {sections.map((s) => (
          <details key={s.id} className="group" data-testid={`recon-${s.id}`}>
            <summary className="px-5 py-3 cursor-pointer flex items-center justify-between hover:bg-secondary/30">
              <div>
                <div className="text-[13px] font-medium">{s.label}</div>
                <div className="text-[11px] text-foreground/55 mt-0.5">{s.hint}</div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[11px] mono uppercase tracking-wider border ${
                s.count > 0
                  ? "bg-amber-50 text-amber-800 border-amber-200"
                  : "bg-emerald-50 text-emerald-700 border-emerald-200"
              }`}>
                {s.count}
              </span>
            </summary>
            {s.count > 0 && (
              <div className="px-5 pb-3 max-h-64 overflow-y-auto">
                <table className="w-full text-[12px]">
                  <thead className="text-foreground/55 text-[10.5px] mono uppercase tracking-wider">
                    <tr>
                      <th className="text-left py-1.5">Ref</th>
                      <th className="text-left">Status</th>
                      <th className="text-right">Amount</th>
                      <th className="text-right">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(s.rows || []).map((r, i) => (
                      <tr key={i} className="border-t border-border/60">
                        <td className="py-1.5 mono text-[11.5px]">{r.ref}</td>
                        <td>{r.status || r.ledger_status} {r.webhook_status ? `→ ${r.webhook_status}` : ""}</td>
                        <td className="text-right tabular-nums">{fmtUsdP(r.amount_usd)}</td>
                        <td className="text-right text-[11px] text-foreground/55">{(r.received_at || r.created_at || "").slice(0, 19).replace("T", " ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </details>
        ))}
      </div>
    </section>
  );
}

function FeeConfigPanel({ fees, onSaved }) {
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (fees?.rails) setDraft(structuredClone(fees.rails)); }, [fees]);

  if (!fees || !draft) return null;

  const onChange = (rail, key, val) => {
    const v = parseInt(val || "0", 10);
    setDraft(prev => ({ ...prev, [rail]: { ...prev[rail], [key]: isNaN(v) ? 0 : Math.max(0, v) } }));
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/bank-rail/fees", { rails: draft });
      toast.success("Fee config saved");
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save fees");
    } finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl border border-border bg-white" data-testid="bank-rail-fees">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Per-rail fees</div>
          <div className="text-[14px] font-medium mt-0.5">
            <Settings2 className="w-3.5 h-3.5 inline mr-1.5 text-foreground/55"/>
            Last updated {fees.updated_at ? new Date(fees.updated_at).toLocaleString() : "—"}
          </div>
        </div>
        {!editing
          ? <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="fees-edit-btn" className="rounded-full">Edit</Button>
          : <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setEditing(false); setDraft(structuredClone(fees.rails)); }} className="rounded-full">Cancel</Button>
              <Button size="sm" onClick={save} disabled={busy} data-testid="fees-save-btn" className="rounded-full bg-foreground text-background hover:bg-foreground/90">
                {busy ? "Saving…" : "Save fees"}
              </Button>
            </div>
        }
      </div>
      <div className="p-5">
        <table className="w-full text-[12.5px]" data-testid="fees-table">
          <thead className="text-foreground/55 text-[10.5px] mono uppercase tracking-wider">
            <tr>
              <th className="text-left py-2">Rail</th>
              <th className="text-right">Flat (¢)</th>
              <th className="text-right">% (bps)</th>
              <th className="text-right">Cap (¢)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(draft).map(([rid, cfg]) => (
              <tr key={rid} className="border-t border-border/60">
                <td className="py-2 font-medium uppercase">{rid}</td>
                {["flat_cents", "pct_bps", "cap_cents"].map((k) => (
                  <td key={k} className="text-right">
                    {editing ? (
                      <Input
                        type="number" min={0}
                        value={cfg[k] ?? 0}
                        onChange={(e) => onChange(rid, k, e.target.value)}
                        data-testid={`fee-${rid}-${k}`}
                        className="w-24 h-8 ml-auto text-right tabular-nums"
                      />
                    ) : (
                      <span className="tabular-nums">{cfg[k] ?? 0}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AuditLogPanel({ audit }) {
  if (!audit) return null;
  return (
    <section className="rounded-2xl border border-border bg-white" data-testid="bank-rail-audit">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Audit log</div>
          <div className="text-[14px] font-medium mt-0.5">
            <ScrollText className="w-3.5 h-3.5 inline mr-1.5 text-foreground/55"/>
            Last {audit.count} ops actions · append-only
          </div>
        </div>
      </div>
      {audit.count === 0 ? (
        <div className="px-5 py-10 text-center text-[13px] text-foreground/55">No bank-rail actions logged yet.</div>
      ) : (
        <div className="max-h-96 overflow-y-auto divide-y divide-border">
          {(audit.rows || []).map((r) => (
            <div key={r.id} className="px-5 py-2.5 flex items-center gap-3 text-[12.5px]" data-testid={`audit-row-${r.id}`}>
              <span className="mono text-[10.5px] uppercase tracking-wider text-foreground/55 w-28 shrink-0">{r.event}</span>
              {r.rail && <span className="mono text-[10.5px] uppercase tracking-wider text-foreground/70 w-16">{r.rail}</span>}
              <span className="flex-1 truncate">{r.actor_email || r.actor_id || "—"}{r.txn_id ? ` · txn ${r.txn_id.slice(0, 8)}…` : ""}{r.details?.reason ? ` · ${r.details.reason}` : ""}</span>
              <span className="text-[10.5px] text-foreground/55 mono">{(r.created_at || "").slice(0, 19).replace("T", " ")}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function ActionDialog({ open, action, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [toRail, setToRail] = useState("ach");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReason(""); setToRail("ach"); setBusy(false);
  }, [open]);

  if (!open || !action) return null;
  const { txn, kind } = action;

  const submit = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true);
    try {
      if (kind === "retry") {
        await api.post(`/admin/bank-rail/stuck/${txn.id}/retry`, { reason });
        toast.success("Transaction queued for retry");
      } else {
        if (toRail === txn.rail) {
          toast.error("Choose a different rail");
          setBusy(false);
          return;
        }
        await api.post(`/admin/bank-rail/stuck/${txn.id}/reroute`, { to_rail: toRail, reason });
        toast.success(`Rerouted to ${toRail.toUpperCase()}`);
      }
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid={`action-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {kind === "retry" ? <Wrench className="w-4 h-4"/> : <ArrowRight className="w-4 h-4"/>}
            {kind === "retry" ? "Retry transaction" : "Reroute to a different rail"}
          </DialogTitle>
          <DialogDescription>
            {txn.ref} · {fmtUsdP(txn.amount_usd)} · currently on <strong>{txn.rail.toUpperCase()}</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {kind === "reroute" && (
            <div>
              <Label>Target rail</Label>
              <Select value={toRail} onValueChange={setToRail}>
                <SelectTrigger data-testid="reroute-target-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ach">ACH (Shield)</SelectItem>
                  <SelectItem value="wire">Wire</SelectItem>
                  <SelectItem value="rtp">RTP / FedNow</SelectItem>
                  <SelectItem value="card">Card (Stripe)</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Reason (≥3 chars · audit-logged)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                      data-testid="action-reason"
                      placeholder={kind === "retry"
                        ? "e.g. ACH cutoff hit at 4pm — retry tomorrow morning"
                        : "e.g. Wire failed twice; falling back to ACH"}/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="action-submit"
                  className="bg-foreground text-background hover:bg-foreground/90">
            {busy ? "Working…" : (kind === "retry" ? "Queue retry" : "Reroute")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
